import { IS_SERVER } from "@/constants";
import { useCustomDataCollector } from "./useCustomDataCollector";
import { ActionEnum, } from "@/types";
import { getCookieValue } from "@/utils";
import { useSections } from "@/contexts/classified";
import { useEffect } from "react";
import { DeviceEnum } from "@/shared/types/devices.type";
let sharedTokenFetchPromise = null;
const envPrefixDash = () => {
    if (IS_SERVER)
        return "";
    return process.env.CONFIG_ENV === "dev" ||
        process.env.CONFIG_ENV === "preview"
        ? `${process.env.CONFIG_ENV}-`
        : "";
};
const customerActionConfig = {
    urlClientToken: `https://${envPrefixDash()}api.immowelt.com/auth/oauth/token`,
    urlCustomerAction: `https://${envPrefixDash()}data.immowelt.de/customeraction/v1/customeractions`,
};
/**
 * @remarks: CustomerActions is a legacy Tracking / Customer Stats solution used on German brands
 * It should be replaced at some point by WL Backend Tracking https://avivgroup.atlassian.net/browse/SEEKERWL-1379
 *
 * See Harvest / legacyTracking for the similar solution for French brands
 */
async function customerAction(customData, action, parameters, trackOnlyOnceName = null) {
    if (customData &&
        customData.customerActionEnabled &&
        !customData.isCypress &&
        !customData.config.previewMode) {
        if (trackOnlyOnceName) {
            if (customData.processedActions.current.has(trackOnlyOnceName))
                return;
            customData.processedActions.current.add(trackOnlyOnceName);
        }
        let clientToken = getCachedClientToken();
        if (!clientToken)
            clientToken = await fetchNewClientToken(customData.tokenString);
        if (clientToken) {
            const isSent = await fetchCustomerAction(customData, clientToken, action, parameters);
            if (!isSent) {
                clientToken = await fetchNewClientToken(customData.tokenString);
                if (clientToken)
                    await fetchCustomerAction(customData, clientToken, action, parameters);
            }
        }
    }
}
export const useCustomerActionExposeSectionView = () => {
    var _a, _b;
    const customData = useCustomDataCollector();
    const images = ((_b = (_a = useSections()) === null || _a === void 0 ? void 0 : _a.gallery) === null || _b === void 0 ? void 0 : _b.images) || [];
    useEffect(() => {
        customerAction(customData, ActionEnum.EXPOSE, [
            { key: "host", value: window.location.host },
            { key: "urlReferrer", value: window.document.referrer },
            {
                key: "placement",
                value: new URL(window.location.href).searchParams.get("placement"),
            },
            { key: "imageCount", value: images.length },
        ], "CustomerActionSectionView");
    }, [customData, images.length]);
};
export const useCustomerAction = () => {
    const customData = useCustomDataCollector();
    const customerActionExposeInteraction = (parameters, trackOnlyOnceName = null) => {
        customerAction(customData, ActionEnum.EXPOSE_INTERACTION, parameters, trackOnlyOnceName);
    };
    const customerActionLeadInteraction = (interaction, value) => {
        customerAction(customData, ActionEnum.LEAD_INTERACTION, [
            { key: "interaction", value: interaction },
            { key: "value", value: value },
        ]);
    };
    const customerActionExposeContact = (parameters) => {
        customerAction(customData, ActionEnum.EXPOSE_CONTACT, parameters);
    };
    const customerActionExposeContactPhoneCall = () => {
        customerAction(customData, ActionEnum.EXPOSE_CONTACT, [
            { key: "type", value: "anruf" },
        ]);
    };
    const customerActionExposeContactPhoneView = () => {
        customerAction(customData, ActionEnum.EXPOSE_CONTACT, [
            { key: "type", value: "telefon" },
        ]);
    };
    return {
        customerActionExposeInteraction,
        customerActionLeadInteraction,
        customerActionExposeContact,
        customerActionExposeContactPhoneView,
        customerActionExposeContactPhoneCall,
    };
};
async function fetchNewClientToken(tokenString) {
    if (!sharedTokenFetchPromise) {
        sharedTokenFetchPromise = (async () => {
            try {
                const secretToken = tokenString ? JSON.parse(tokenString) : null;
                if (!secretToken) {
                    return null;
                }
                const secret = window.btoa(`${secretToken.clientId}:${secretToken.clientSecret}`);
                const response = await fetch(customerActionConfig.urlClientToken, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        Authorization: `Basic ${secret}`,
                    },
                    body: "grant_type=client_credentials",
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch client token");
                }
                const { access_token } = await response.json();
                const currentTime = Date.now();
                const newTokenData = {
                    value: { clientId: secretToken.clientId, token: access_token },
                    expiry: currentTime + 3600000, // 1 hour
                };
                localStorage.setItem("clientToken", JSON.stringify(newTokenData));
                return newTokenData.value;
            }
            catch (error) {
                localStorage.removeItem("clientToken");
                return null;
            }
            finally {
                sharedTokenFetchPromise = null;
            }
        })();
    }
    return sharedTokenFetchPromise;
}
async function fetchCustomerAction(
// eslint-disable-next-line @typescript-eslint/no-explicit-any
customData, clientToken, action, parameters) {
    var _a, _b;
    const requestParameters = {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            "X-Tenant": "immowelt",
            "X-Roles": "clickstreamer",
            "X-Client-Id": clientToken === null || clientToken === void 0 ? void 0 : clientToken.clientId,
            Authorization: `Bearer ${clientToken === null || clientToken === void 0 ? void 0 : clientToken.token}`,
        },
        body: JSON.stringify({
            Action: action,
            Source: "Expose-UI",
            Version: "whitelabel-cdp",
            Device: customData.isDesktop ? DeviceEnum.DESKTOP : DeviceEnum.SMARTPHONE,
            FitScoreConsent: !!getCookieValue("xy"),
            ProfileId: getCookieValue("wd"),
            Application: `${customData.portal}.web`,
            Parameters: [
                ...parameters,
                { key: "globalObjectKey", value: customData.legacyId },
                {
                    key: "globalUserIdOfferer",
                    value: ((_a = customData.targetingVars) === null || _a === void 0 ? void 0 : _a.IMMO_GLOBALUSERID)
                        ? Number((_b = customData.targetingVars) === null || _b === void 0 ? void 0 : _b.IMMO_GLOBALUSERID)
                        : 0,
                },
            ],
        }),
    };
    try {
        return (await fetch(customerActionConfig.urlCustomerAction, requestParameters)).ok;
    }
    catch (error) {
        return false;
    }
}
function getCachedClientToken() {
    try {
        const cachedTokenDataString = localStorage.getItem("clientToken");
        if (!cachedTokenDataString)
            return null;
        const cachedTokenData = JSON.parse(cachedTokenDataString);
        if (Date.now() > cachedTokenData.expiry) {
            localStorage.removeItem("clientToken");
            return null;
        }
        return cachedTokenData.value;
    }
    catch (error) {
        return null;
    }
}
